// SeeTex Web — Browser-based LaTeX Paper Editor

import { useState, useEffect, useRef, useCallback, Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { PaperEditor } from "../src/components/PaperEditor";
import type { PaperEditorHandle } from "../src/components/PaperEditor";
import { SnippetReference } from "../src/components/SnippetReference";
import { useAutocomplete } from "../src/contexts/AutocompleteContext";
import { listDocuments, createDocument, importTexFile, openFromFilesystem, setCloudSync, getCloudSync, mergeCloudDocuments, saveFile, getActiveDocumentId, setActiveDocumentId, deleteDocument, compileToPdf, getDocumentContent, defaultFileContents, type StoredDocument } from "./services/file-sync";
import { ImageUploadModal } from "../src/components/ImageUploadModal";
import { setProjectImage, clearProjectImages, getAllProjectImages } from "../src/editor/figure-node";
import { loadDocImages, setManifest } from "./services/image-store";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { UserMenu } from "./components/UserMenu";
import { ApiKeyModal } from "./components/ApiKeyModal";
import { GuestSignUpModal } from "./components/GuestSignUpModal";
import { UpgradeModal } from "./components/UpgradeModal";
import { FeedbackButton } from "./components/FeedbackButton";
import { useAuth } from "./contexts/AuthContext";
import { usePlan, incrementScanCount, SCAN_LIMIT } from "./hooks/usePlan";
import { createCloudSync } from "./services/cloud-sync";
import { getFirebaseApp, isDemoMode } from "./config/firebase";
import { scanPdfToLatex, getStoredApiKey } from "../src/services/pdf-scanner";
import type { ScanProgress } from "../src/services/pdf-scanner";
import { startTracking, stopTracking } from "./services/usage-tracker";
// Dev-only replay panel — lazy-loaded so it's excluded from production bundles entirely.
import { lazy, Suspense } from "react";
const ReplayPanel = import.meta.env.DEV
    ? lazy(() => import('./components/ReplayPanel').then(m => ({ default: m.ReplayPanel })))
    : () => null;

// ── Error boundary ───────────────────────────────────────────────────
class EditorErrorBoundary extends Component<
    { children: ReactNode },
    { error: Error | null }
> {
    state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("[SeeTex Web] Editor crashed:", error, info.componentStack);
    }

    render() {
        if (this.state.error) {
            return (
                <div style={{ padding: 32, color: "#e74c3c", fontFamily: "monospace" }}>
                    <h2>Editor Error</h2>
                    <pre style={{ whiteSpace: "pre-wrap" }}>{this.state.error.message}</pre>
                    <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, opacity: 0.7 }}>
                        {this.state.error.stack}
                    </pre>
                    <button
                        onClick={() => this.setState({ error: null })}
                        style={{ marginTop: 12, padding: "6px 16px" }}
                    >
                        Retry
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// ── Error Modal ────────────────────────────────────────────────────
function ErrorModal({ title, message, onClose }: { title: string; message: string; onClose: () => void }) {
    return (
        <div className="confirm-modal-overlay" onClick={onClose}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="confirm-modal-title">{title}</h3>
                <p className="confirm-modal-message">{message}</p>
                <div className="confirm-modal-actions">
                    <button className="confirm-modal-confirm confirm-modal-confirm--primary" onClick={onClose}>OK</button>
                </div>
            </div>
        </div>
    );
}

// ── Confirm Modal ───────────────────────────────────────────────────
function ConfirmModal({
    title,
    message,
    confirmLabel = "Delete",
    onConfirm,
    onCancel,
}: {
    title: string;
    message: string;
    confirmLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    return (
        <div className="confirm-modal-overlay" onClick={onCancel}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="confirm-modal-title">{title}</h3>
                <p className="confirm-modal-message">{message}</p>
                <div className="confirm-modal-actions">
                    <button className="confirm-modal-cancel" onClick={onCancel}>Cancel</button>
                    <button className="confirm-modal-confirm" onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}

// ── Input Modal ──────────────────────────────────────────────────────
function InputModal({
    title,
    placeholder,
    defaultValue = "",
    confirmLabel = "Create",
    onConfirm,
    onCancel,
}: {
    title: string;
    placeholder?: string;
    defaultValue?: string;
    confirmLabel?: string;
    onConfirm: (value: string) => void;
    onCancel: () => void;
}) {
    const [value, setValue] = useState(defaultValue);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.select();
    }, []);

    const submit = () => {
        const trimmed = value.trim();
        if (trimmed) onConfirm(trimmed);
    };

    return (
        <div className="confirm-modal-overlay" onClick={onCancel}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="confirm-modal-title">{title}</h3>
                <input
                    ref={inputRef}
                    className="input-modal-field"
                    value={value}
                    placeholder={placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") submit();
                        if (e.key === "Escape") onCancel();
                    }}
                    autoFocus
                />
                <div className="confirm-modal-actions">
                    <button className="confirm-modal-cancel" onClick={onCancel}>Cancel</button>
                    <button
                        className="confirm-modal-confirm confirm-modal-confirm--primary"
                        onClick={submit}
                        disabled={!value.trim()}
                    >{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}

// Document selector for managing multiple documents
function DocumentSelector({
    onSelect,
    onDelete,
    onImportWithImages,
    isPro,
    onImageSyncNeeded,
}: {
    onSelect: (doc: StoredDocument) => void;
    onDelete: (docId: string) => void;
    onImportWithImages: (doc: StoredDocument, referencedFiles: string[]) => void;
    isPro: boolean;
    onImageSyncNeeded: () => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") setIsOpen(false); };
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false);
        };
        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);
    const [, forceUpdate] = useState({});
    const [pendingDelete, setPendingDelete] = useState<StoredDocument | null>(null);
    const [showNewDocModal, setShowNewDocModal] = useState(false);
    const [fsError, setFsError] = useState<string | null>(null);
    const docs = listDocuments().sort((a, b) => b.lastModified - a.lastModified);
    const fsAccessSupported = "showOpenFilePicker" in window;

    const handleNewDoc = () => {
        setIsOpen(false);
        setShowNewDocModal(true);
    };

    const handleNewDocConfirm = (name: string) => {
        const doc = createDocument(name);
        onSelect(doc);
        setShowNewDocModal(false);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const doc = await importTexFile(file);
            // Check for \includegraphics references
            const imgRefs = Array.from(doc.content.matchAll(/\\includegraphics(?:\[[^\]]*\])?\{([^}]+)\}/g)).map(m => m[1]);
            if (imgRefs.length > 0) {
                onImportWithImages(doc, imgRefs);
            } else {
                onSelect(doc);
            }
            setIsOpen(false);
        }
    };

    const handleOpenFolder = async () => {
        let dirHandle: FileSystemDirectoryHandle;
        try {
            dirHandle = await (window as any).showDirectoryPicker({ mode: "read" });
        } catch (e) {
            if ((e as Error).name !== "AbortError") setFsError((e as Error).message);
            return;
        }
        // Recursively collect all files from the directory
        const allEntries: { path: string; file: File }[] = [];
        async function collectEntries(handle: FileSystemDirectoryHandle, prefix: string) {
            for await (const [name, child] of (handle as any)) {
                const childPath = prefix ? `${prefix}/${name}` : name;
                if ((child as FileSystemHandle).kind === "file") {
                    const file = await (child as FileSystemFileHandle).getFile();
                    allEntries.push({ path: childPath, file });
                } else if ((child as FileSystemHandle).kind === "directory") {
                    await collectEntries(child as FileSystemDirectoryHandle, childPath);
                }
            }
        }
        await collectEntries(dirHandle, "");
        if (allEntries.length === 0) return;
        // Find the main .tex file (prefer the one containing \begin{document})
        const texEntries = allEntries.filter(e => e.file.name.endsWith(".tex"));
        if (texEntries.length === 0) { setFsError("No .tex file found in the selected folder."); return; }
        let mainTexEntry = texEntries.find(e => e.file.name === "main.tex") ?? texEntries[0];
        if (texEntries.length > 1) {
            for (const te of texEntries) {
                const sample = await te.file.slice(0, 4096).text();
                if (sample.includes("\\begin{document}")) { mainTexEntry = te; break; }
            }
        }
        const doc = await importTexFile(mainTexEntry.file);
        // Auto-match all figure files from the folder — no second modal needed since we
        // already have the full directory contents from showDirectoryPicker().
        const FIGURE_EXTS = new Set([".png", ".jpg", ".jpeg", ".gif", ".svg", ".pdf", ".eps", ".bmp", ".tiff", ".tif", ".webp"]);
        const figureEntries = allEntries.filter(e => {
            const ext = e.file.name.toLowerCase().match(/\.[^./\\]+$/)?.[0];
            return ext ? FIGURE_EXTS.has(ext) : false;
        });
        if (figureEntries.length > 0) {
            const imgRefs = Array.from(doc.content.matchAll(/\\includegraphics(?:\[[^\]]*\])?\{([^}]+)\}/g)).map(m => m[1]);
            const byPath = new Map(figureEntries.map(e => [e.path, e]));
            const byPathNoExt = new Map(figureEntries.map(e => [e.path.replace(/\.[^./]+$/, ""), e]));
            const byBasename = new Map(figureEntries.map(e => [e.file.name, e]));
            const byBasenameNoExt = new Map(figureEntries.map(e => [e.file.name.replace(/\.[^.]+$/, ""), e]));
            const refsToMatch = imgRefs.length > 0 ? imgRefs : figureEntries.map(e => e.path);
            const autoImages: Record<string, string> = {};
            for (const ref of refsToMatch) {
                const base = ref.split("/").pop() ?? ref;
                const entry = byPath.get(ref) ?? byPathNoExt.get(ref) ?? byBasename.get(base) ?? byBasenameNoExt.get(base.replace(/\.[^.]+$/, ""));
                if (entry && !autoImages[entry.path]) {
                    const dataUrl = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(entry.file); });
                    autoImages[entry.path] = dataUrl;
                }
            }
            if (Object.keys(autoImages).length > 0) {
                // Pro: uploads to Firebase Storage + persists imageFiles manifest to Firestore.
                // Free: local IndexedDB only.
                await saveFile(doc.id, undefined, autoImages, isPro);
                doc.images = autoImages;
                if (!isPro) onImageSyncNeeded();
            }
        }
        onSelect(doc);
        setIsOpen(false);
    };

    const handleOpenFromFs = async () => {
        try {
            const doc = await openFromFilesystem();
            if (doc) {
                // Check for \includegraphics references
                const imgRefs = Array.from(doc.content.matchAll(/\\includegraphics(?:\[[^\]]*\])?\{([^}]+)\}/g)).map(m => m[1]);
                if (imgRefs.length > 0) {
                    onImportWithImages(doc, imgRefs);
                } else {
                    onSelect(doc);
                }
                setIsOpen(false);
            }
        } catch (e) {
            setFsError((e as Error).message);
        }
    };

    const handleDelete = (e: React.MouseEvent, doc: StoredDocument) => {
        e.stopPropagation();
        setPendingDelete(doc);
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        deleteDocument(pendingDelete.id);
        onDelete(pendingDelete.id);
        setPendingDelete(null);
        forceUpdate({});
    };

    return (
        <>
            <div className="doc-selector" ref={dropdownRef}>
                <button
                    className="doc-selector-btn"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                    Documents
                </button>
                {isOpen && (
                    <div className="doc-selector-dropdown">
                        <button onClick={handleNewDoc}>+ New Document</button>
                        {fsAccessSupported ? (
                            <button onClick={handleOpenFromFs}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg> Open file…</button>
                        ) : (
                            <label className="doc-upload-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                Import .tex
                                <input
                                    type="file"
                                    accept=".tex"
                                    onChange={handleFileUpload}
                                    style={{ display: "none" }}
                                />
                            </label>
                        )}
                        {"showDirectoryPicker" in window && (
                            <button onClick={handleOpenFolder}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /><path d="M9 13h6" /><path d="M12 10v6" /></svg>
                                Open folder…
                            </button>
                        )}
                        {docs.length > 0 && <hr />}
                        <div className="doc-selector-list">
                            {docs.map((doc) => (
                                <div key={doc.id} className="doc-selector-item">
                                    <button
                                        className="doc-selector-item-name"
                                        onClick={() => {
                                            onSelect(doc);
                                            setIsOpen(false);
                                        }}
                                    >
                                        {doc.name}
                                    </button>
                                    <button
                                        className="doc-selector-item-delete"
                                        onClick={(e) => handleDelete(e, doc)}
                                        title="Delete document"
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {pendingDelete && (
                <ConfirmModal
                    title="Delete document?"
                    message={`"${pendingDelete.name}" will be permanently deleted. This cannot be undone.`}
                    confirmLabel="Delete"
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                />
            )}
            {showNewDocModal && (
                <InputModal
                    title="New document"
                    placeholder="Document name"
                    defaultValue="Untitled"
                    confirmLabel="Create"
                    onConfirm={handleNewDocConfirm}
                    onCancel={() => setShowNewDocModal(false)}
                />
            )}
            {fsError && (
                <ErrorModal
                    title="Could not open file"
                    message={fsError}
                    onClose={() => setFsError(null)}
                />
            )}
        </>
    );
}

export function WebApp() {
    const [showReference, setShowReference] = useState(true);
    const { autocompleteEnabled, setAutocompleteEnabled, hintsEnabled, setHintsEnabled } = useAutocomplete();
    const [activeDocId, setActiveDocId] = useState<string | null>(getActiveDocumentId);
    const [, forceDocListUpdate] = useState({});
    const { user } = useAuth();
    const plan = usePlan(user?.uid ?? null);
    const editorRef = useRef<PaperEditorHandle>(null);
    const pdfInputRef = useRef<HTMLInputElement>(null);

    // ── Ctrl+. / Cmd+. → toggle auto-expand ────────────────────────────────
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "." && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                setAutocompleteEnabled(!autocompleteEnabled);
            }
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [autocompleteEnabled, setAutocompleteEnabled]);

    // PDF scan state
    const [showApiKeyModal, setShowApiKeyModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showImageSyncWarning, setShowImageSyncWarning] = useState(false);
    const [showGuestSignUp, setShowGuestSignUp] = useState(false);
    const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null);
    const [scanError, setScanError] = useState<string | null>(null);

    // Image upload modal state — shown after importing a .tex file with \includegraphics
    const [imageUploadState, setImageUploadState] = useState<{
        doc: StoredDocument;
        referencedFiles: string[];
    } | null>(null);

    // Dark mode — persisted to localStorage
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem("seetex-theme") === "dark";
    });
    const toggleDarkMode = () => {
        setDarkMode((prev) => {
            const next = !prev;
            document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
            localStorage.setItem("seetex-theme", next ? "dark" : "light");
            return next;
        });
    };
    // Apply persisted theme on mount
    useEffect(() => {
        const saved = localStorage.getItem("seetex-theme");
        if (saved) document.documentElement.setAttribute("data-theme", saved);
    }, []);

    // Track all user sessions (including anonymous/guest)
    useEffect(() => {
        if (user) {
            startTracking(user.uid, user.isAnonymous);
        }
        return () => stopTracking();
    }, [user]);

    // Read the active document content once on first render so PaperEditor
    // can use it as its initial TipTap content (no async race).
    const [initialLatex] = useState<string | undefined>(() => {
        const id = getActiveDocumentId();
        if (!id) return defaultFileContents;
        const content = getDocumentContent(id);
        return content || undefined;
    });

    // Load images for the initial document from IndexedDB after mount.
    // FigureNodeView components self-heal via the imageLoadListeners subscription.
    useEffect(() => {
        const id = getActiveDocumentId();
        if (!id) return;
        loadDocImages(id).then((imgs) => {
            for (const [filename, dataUrl] of Object.entries(imgs)) {
                setProjectImage(filename, dataUrl);
            }
        }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Save status indicator
    type SaveStatus = "idle" | "saving" | "saved" | "error";
    const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Called immediately on every editor keystroke (before the 400ms debounce).
    // Shows "Saving…" right away so the indicator is visible while the user types.
    const handleDirty = useCallback(() => {
        setSaveStatus("saving");
    }, []);

    // Auto-save editor content back to localStorage + Firestore on every change
    const handleLatexChange = useCallback(async (latexFull: string) => {
        if (!activeDocId) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        const currentImages = Object.fromEntries(getAllProjectImages());
        try {
            await saveFile(activeDocId, latexFull, Object.keys(currentImages).length > 0 ? currentImages : undefined, plan.isPro);
            setSaveStatus("saved");
        } catch {
            setSaveStatus("error");
        }
        saveTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2500);
    }, [activeDocId, plan.isPro]);

    // When the user picks a document from the list, load it into the editor
    const handleDocumentSelect = useCallback((doc: StoredDocument) => {
        setActiveDocId(doc.id);
        setActiveDocumentId(doc.id);
        // Load images from IndexedDB first, then import LaTeX so figure nodes
        // have images available when they first render.
        clearProjectImages();
        // If Firestore gave us the list of image filenames, populate the local
        // manifest so loadDocImages can find them in Firebase Storage on this device.
        if (doc.imageFiles && doc.imageFiles.length > 0) {
            setManifest(doc.id, doc.imageFiles);
        }
        // Restore any images already on the in-memory doc (folder import path)
        if (doc.images) {
            for (const [filename, dataUrl] of Object.entries(doc.images)) {
                setProjectImage(filename, dataUrl);
            }
        }
        // Load content from its dedicated localStorage key (lazy — not in the doc object).
        // If localStorage is empty (e.g. fresh browser, cleared data), pull from Firestore.
        const localContent = getDocumentContent(doc.id);
        let content = doc.content || localContent;
        if (content) {
            editorRef.current?.importLatex(content);
        } else {
            const cs = getCloudSync();
            if (cs) {
                cs.loadDocument(doc.id).then((cloudDoc) => {
                    const loaded = cloudDoc?.content || defaultFileContents;
                    editorRef.current?.importLatex(loaded);
                }).catch(() => {
                    editorRef.current?.importLatex(defaultFileContents);
                });
            } else {
                editorRef.current?.importLatex(defaultFileContents);
            }
        }
        // Load images from IndexedDB / Firebase Storage.
        loadDocImages(doc.id).then((imgs) => {
            for (const [filename, dataUrl] of Object.entries(imgs)) {
                setProjectImage(filename, dataUrl);
            }
        }).catch(() => {});
        forceDocListUpdate({});
    }, []);

    // PDF scan handlers
    const handleScanClick = () => {
        if (!plan.isPro && plan.scanCount >= SCAN_LIMIT) { setShowUpgradeModal(true); return; }
        const key = getStoredApiKey();
        if (!key) { setShowApiKeyModal(true); return; }
        pdfInputRef.current?.click();
    };

    const handlePdfSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const key = getStoredApiKey();
        if (!key) { setShowApiKeyModal(true); return; }
        try {
            const latex = await scanPdfToLatex(file, key, (p) => setScanProgress(p));
            const docName = file.name.replace(/\.pdf$/i, "") || "Scanned Document";
            const newDoc = createDocument(docName);
            setActiveDocId(newDoc.id);
            clearProjectImages();
            editorRef.current?.importLatex(latex);
            forceDocListUpdate({});
            if (user && !plan.isPro) await incrementScanCount(user.uid);
        } catch (err) {
            setScanError(err instanceof Error ? err.message : String(err));
        } finally {
            setScanProgress(null);
        }
    };

    const handleApiKeySaved = (_key: string) => {
        setShowApiKeyModal(false);
        pdfInputRef.current?.click();
    };

    const scanLabel = (() => {
        if (!scanProgress) return "Scan PDF";
        if (scanProgress.stage === "rendering")
            return `Reading ${scanProgress.page}/${scanProgress.total}…`;
        return `Scanning ${scanProgress.page}/${scanProgress.total}…`;
    })();

    // Wire Firestore cloud sync whenever the user changes
    // Skip for anonymous/guest users — their data lives locally only.
    useEffect(() => {
        if (user && !user.isAnonymous && !isDemoMode()) {
            const app = getFirebaseApp();
            const cs = createCloudSync(app, user.uid);
            setCloudSync(cs);
            // Merge cloud → local, then push any local docs not yet in cloud
            cs.loadDocuments().then((cloudDocs) => {
                if (mergeCloudDocuments(cloudDocs)) forceDocListUpdate({});

                // After merge, refresh the active document in the editor.
                // initialLatex may have defaultFileContents if localStorage was empty.
                const activeId = getActiveDocumentId();
                if (activeId) {
                    const restored = getDocumentContent(activeId);
                    if (restored && editorRef.current) {
                        editorRef.current.importLatex(restored);
                    }
                }
                // Upload local docs that the cloud doesn't have yet
                const cloudIds = new Set(cloudDocs.map((d) => d.id));
                const localDocs = listDocuments();
                const toUpload = localDocs.filter((d) => !cloudIds.has(d.id));
                for (const doc of toUpload) {
                    // listDocuments() returns docs with content:"" (stored separately).
                    // Read the real content from localStorage before uploading.
                    const content = getDocumentContent(doc.id);
                    cs.saveDocument({ ...doc, content: content || doc.content }).catch((e) => {
                        console.error("[SeeTex] Firestore save failed:", e?.code, e?.message);
                    });
                }

                // Repair: if Firestore has docs with empty content but localStorage
                // has the real content, re-upload so other devices can access it.
                for (const cloudDoc of cloudDocs) {
                    if (!cloudDoc.content) {
                        const localContent = getDocumentContent(cloudDoc.id);
                        if (localContent) {
                            cs.saveDocument({ ...cloudDoc, content: localContent }).catch(() => {});
                        }
                    }
                }

                // Auth is now resolved — retry loading images for the active document.
                // The initial loadDocImages call at mount fires before Firebase Auth is
                // ready (currentUser is null), so images from Storage are skipped there.
                if (activeId) {
                    const activeDoc = listDocuments().find((d) => d.id === activeId);
                    if (activeDoc?.imageFiles && activeDoc.imageFiles.length > 0) {
                        setManifest(activeId, activeDoc.imageFiles);
                    }
                    loadDocImages(activeId).then((imgs) => {
                        for (const [filename, dataUrl] of Object.entries(imgs)) {
                            setProjectImage(filename, dataUrl);
                        }
                    }).catch(() => { });
                }
            }).catch((e) => {
                console.error("[SeeTex] Firestore load failed:", e?.code, e?.message);
            });
            return () => cs.unsubscribe();
        } else {
            setCloudSync(null);
        }
    }, [user]);

    return (
        <ProtectedRoute>
            <div className="app">
                <header className="app-header">
                    <span className="app-logo">∫</span>
                    <h1>SeeTex</h1>
                    <div style={{ flex: 1 }} />
                    <DocumentSelector
                        onSelect={handleDocumentSelect}
                        isPro={plan.isPro}
                        onImageSyncNeeded={() => setShowImageSyncWarning(true)}
                        onDelete={(id) => {
                            if (activeDocId === id) {
                                const remaining = listDocuments();
                                if (remaining.length > 0) {
                                    handleDocumentSelect(remaining[0]);
                                } else {
                                    setActiveDocId(null);
                                }
                            }
                            forceDocListUpdate({});
                        }} onImportWithImages={(doc, referencedFiles) => {
                            setImageUploadState({ doc, referencedFiles });
                        }} />
                    {saveStatus !== "idle" && (
                        <span className={`save-status save-status--${saveStatus}`}>
                            {saveStatus === "saving" && (
                                <>
                                    <svg className="save-status-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
                                    Saving
                                </>
                            )}
                            {saveStatus === "saved" && (
                                <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    Saved
                                </>
                            )}
                            {saveStatus === "error" && (
                                <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                    Error saving
                                </>
                            )}
                        </span>
                    )}
                    <button
                        className="scan-btn"
                        onClick={handleScanClick}
                        disabled={!!scanProgress}
                        title="Scan a PDF and convert to LaTeX using Claude AI"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                        {scanLabel}
                    </button>
                    <input
                        ref={pdfInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        style={{ display: "none" }}
                        onChange={handlePdfSelected}
                    />
                    <label
                        className="toggle-switch-label"
                        title={hintsEnabled ? "Autocomplete suggestions appear as you type. Click to hide them." : "Autocomplete suggestions are hidden. Click to show them."}
                    >
                        <span className="toggle-switch-text">Suggestions</span>
                        <span className={`toggle-switch ${hintsEnabled ? "toggle-switch--on" : ""}`} onClick={() => setHintsEnabled(!hintsEnabled)}>
                            <span className="toggle-switch-knob" />
                        </span>
                    </label>
                    <label
                        className="toggle-switch-label"
                        title={autocompleteEnabled ? "Math triggers auto-fire as you type. Click to switch to manual (Enter to expand). (Cmd+.)" : "Manual mode: press Enter to expand triggers. Click to switch to auto-fire. (Cmd+.)"}
                    >
                        <span className="toggle-switch-text">Auto-expand</span>
                        <span className={`toggle-switch ${autocompleteEnabled ? "toggle-switch--on" : ""}`} onClick={() => setAutocompleteEnabled(!autocompleteEnabled)}>
                            <span className="toggle-switch-knob" />
                        </span>
                    </label>
                    <button
                        className="reference-toggle-btn"
                        onClick={() => setShowReference(!showReference)}
                        title="Toggle Snippet Reference"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
                        {showReference ? "Hide Reference" : "Reference"}
                    </button>
                    <button
                        className="theme-toggle-btn"
                        onClick={toggleDarkMode}
                        title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {darkMode ? (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
                        ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                        )}
                    </button>
                    <UserMenu onUpgrade={plan.isPro ? undefined : () => setShowUpgradeModal(true)} onSignUp={() => setShowGuestSignUp(true)} />
                </header>

                <div className="app-body">
                    <SnippetReference
                        isOpen={showReference}
                        onClose={() => setShowReference(false)}
                    />

                    <main className="app-stage app-stage--paper">
                        <EditorErrorBoundary>
                            <PaperEditor ref={editorRef} initialLatex={initialLatex} onLatexFullChange={handleLatexChange} onDirty={handleDirty} compilePdf={compileToPdf} documentName={activeDocId ? listDocuments().find(d => d.id === activeDocId)?.name : undefined} />
                        </EditorErrorBoundary>
                    </main>
                </div>
            </div>
            {showApiKeyModal && (
                <ApiKeyModal
                    existingKey={getStoredApiKey()}
                    onSave={handleApiKeySaved}
                    onClose={() => setShowApiKeyModal(false)}
                />
            )}
            {showImageSyncWarning && (
                <div className="image-sync-warning" role="alert">
                    <span className="image-sync-warning-icon">⚠️</span>
                    <span className="image-sync-warning-text">
                        Images are saved <strong>on this device only</strong> and won't appear if you log in elsewhere.
                    </span>
                    <button
                        className="image-sync-warning-upgrade"
                        onClick={() => { setShowImageSyncWarning(false); setShowUpgradeModal(true); }}
                    >
                        Upgrade to sync →
                    </button>
                    <button className="image-sync-warning-close" onClick={() => setShowImageSyncWarning(false)} aria-label="Dismiss">✕</button>
                </div>
            )}
            {showUpgradeModal && (
                <UpgradeModal context="images" onClose={() => setShowUpgradeModal(false)} />
            )}
            {showGuestSignUp && (
                <GuestSignUpModal onClose={() => setShowGuestSignUp(false)} />
            )}
            {scanError && (
                <ErrorModal
                    title="Scan failed"
                    message={scanError}
                    onClose={() => setScanError(null)}
                />
            )}
            {scanProgress && (
                <div className="scan-progress-overlay">
                    <div className="scan-progress-box">
                        <svg className="save-status-spinner" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
                        <span>{scanLabel}</span>
                    </div>
                </div>
            )}
            {imageUploadState && (
                <ImageUploadModal
                    referencedFiles={imageUploadState.referencedFiles}
                    onConfirm={(images) => {
                        const doc = imageUploadState.doc;
                        // Store images in the document
                        saveFile(doc.id, undefined, images, plan.isPro);
                        doc.images = { ...doc.images, ...images };
                        // Load into project images
                        for (const [filename, dataUrl] of Object.entries(images)) {
                            setProjectImage(filename, dataUrl);
                        }
                        handleDocumentSelect(doc);
                        setImageUploadState(null);
                    }}
                    onSkip={() => {
                        handleDocumentSelect(imageUploadState.doc);
                        setImageUploadState(null);
                    }}
                />
            )}
            {import.meta.env.DEV && <Suspense fallback={null}><ReplayPanel editorRef={editorRef} /></Suspense>}
            <FeedbackButton />
        </ProtectedRoute>
    );
}

export default WebApp;
